import { TitularTooltip } from '@campfire/titular-tooltip';
import React, { memo, useMemo } from 'react';
import Icon from '@mdi/react';
import { useRouteMatch } from 'react-router';
import { Typography, Box } from '@material-ui/core';
import { MoreHoriz, DragIndicator } from '@material-ui/icons';
import { HoverLink } from '@campfire/hover-link';
import { Link } from 'react-router-dom';
import { ResourcesListItem } from './ResourcesListItem';
import { SupportCategory } from '../__generated__/SupportCategory';
import { useSupportContext } from '../SupportContext';
import { iconList } from '../../../../common/icons/RoleIconList';

const RESOURCES_PER_CATEGORY = 4;

interface CategoriesListItemProps {
  category: SupportCategory;
  reorderMode?: boolean;
}

const CategoriesListItem = memo((props: CategoriesListItemProps) => {
  const { category, reorderMode } = props;
  const { isAdmin } = useSupportContext();
  const match = useRouteMatch();

  const sortedResources = useMemo(() => {
    const resources = [...category.supportResources];
    resources.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
    return resources;
  }, [category.supportResources]);

  const truncatedResources = sortedResources.slice(0, RESOURCES_PER_CATEGORY);
  const hasMore = category.supportResources.length > RESOURCES_PER_CATEGORY;
  const categoryColour = (category as any).colour;
  const categoryIcon = (category as any).icon;

  return (
    <Box
      style={{
        background: '#fff',
        borderRadius: 12,
        marginBottom: 20,
        border: '1px solid #e8e8e8',
        overflow: 'hidden',
      }}
    >
      {/* Category header */}
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        style={{
          padding: '18px 24px',
          borderBottom: category.supportResources.length ? '1px solid #f0f0f0' : undefined,
        }}
      >
        <Box display='flex' alignItems='center'>
          {/* Drag handle for reorder mode */}
          {reorderMode ? (
            <Box display='flex' alignItems='center' style={{ color: '#bbb', cursor: 'grab', marginRight: 8 }}>
              <DragIndicator style={{ fontSize: 20 }} />
            </Box>
          ) : null}
          {/* Vertical colour accent - only show if no icon */}
          {!(categoryIcon && iconList[categoryIcon]) ? (
            <Box
              style={{
                width: 4,
                height: 28,
                borderRadius: 2,
                backgroundColor: categoryColour || '#7047EB',
                flexShrink: 0,
                marginRight: 14,
              }}
            />
          ) : null}
          {categoryIcon && iconList[categoryIcon] ? (
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              mr={1.5}
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                backgroundColor: `${categoryColour || '#7047EB'}20`,
                flexShrink: 0,
              }}
            >
              <Icon path={iconList[categoryIcon]} size={0.85} color={categoryColour || '#7047EB'} />
            </Box>
          ) : null}
          <HoverLink
            to={match?.url ? `${match.url}/${category.supportCategoryId}` : ''}
            color='textPrimary'
            hoverColor='textPrimary'
            disableUnderline
            data-track='resources-landingppage-category-title'
          >
            <Typography
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#333',
              }}
            >
              {category.title}
            </Typography>
          </HoverLink>
        </Box>
        {isAdmin ? (
          <HoverLink
            data-track='resources-landingppage-edit-category'
            to={match?.url ? `${match.url}/${category.supportCategoryId}?cedit=1` : ''}
            color='primary'
          >
            <Typography style={{ fontSize: 13, color: '#7047EB' }}>{'Edit Category'}</Typography>
          </HoverLink>
        ) : null}
      </Box>

      {/* Resources list */}
      {category.supportResources.length ? (
        <Box>
          {truncatedResources.map((resource, idx) => (
            <Link
              data-track='resources-landingppage-resource-item'
              key={resource.supportResourceId}
              to={{
                pathname:
                  match !== null ? `${match.url}/${category.supportCategoryId}/${resource.supportResourceId}` : '',
                state: { returnSlug: match?.url },
              }}
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              <ResourcesListItem
                resource={resource}
                borderBottom={idx !== truncatedResources.length - 1}
                showUploadDate
              />
            </Link>
          ))}
        </Box>
      ) : (
        <Box style={{ padding: '32px 24px' }} display='flex' justifyContent='center'>
          <TitularTooltip
            placement='right'
            title='This category is empty'
            description='No resources have been added to this category yet.'
          >
            <Typography display='inline' style={{ fontSize: 13, color: '#999' }}>
              {'Nothing to show here.'}
            </Typography>
          </TitularTooltip>
        </Box>
      )}

      {/* View all link */}
      {hasMore ? (
        <Box display='flex' justifyContent='center' style={{ padding: 14 }}>
          <HoverLink
            data-track='resources-landingppage-view-all-items'
            to={match?.url ? `${match.url}/${category.supportCategoryId}` : ''}
            color='primary'
          >
            <Box display='flex' alignItems='center' style={{ gap: 4 }}>
              <Typography style={{ fontSize: 13, color: '#7047EB' }}>
                {`View all ${category.supportResources.length} items`}
              </Typography>
              <MoreHoriz style={{ fontSize: 16, color: '#7047EB' }} />
            </Box>
          </HoverLink>
        </Box>
      ) : null}
    </Box>
  );
});

export { CategoriesListItem };
